import * as React from 'react';
import { useState } from 'react';
import { Alert, Button, ControlLabel, FormControl, FormGroup, InputGroup, Panel } from 'react-bootstrap';
import styled from 'styled-components';
import { FileDocumentType } from '@nkr/enums/document-type';
import { formatMoneyInput, parseMoneyInput } from '../../common/money-input';
import VerifyIncomeActions from './VerifyIncomeActions';

interface IProps {
    taxYear: number;
    taxGross: number;
    hasTaxDoc: boolean;
    hasAffidavit: boolean;
    viewingTax: boolean;
    viewingAffidavit: boolean;
    missingDocs: string[];
    canVerify: boolean;
    onTaxYearChange: (year: number) => void;
    onTaxGrossChange: (gross: number) => void;
    onOpenDoc: (docType: number) => void;
    onDecline: () => void;
    onVerify: () => void;
}

const FormWrapper = styled.div`
    .panel-body .control-label {
        color: #475569;
        font-weight: 600;
    }
    .panel-body .form-control {
        border-radius: 6px;
        border-color: #e2e8f0;
    }
    .panel-body .form-control:focus {
        border-color: #94a3b8;
        box-shadow: 0 0 0 2px rgba(148,163,184,0.2);
    }
    .input-group-addon {
        border-radius: 6px 0 0 6px;
        border-color: #e2e8f0;
        background: #f8fafc;
    }
    .selected-btn {
        border: 1px solid #3b82f6;
        background: #eff6ff;
        color: #1d4ed8;
    }
`;

const CardPanel = styled(Panel)`
    margin-top: 12px;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    box-shadow: 0 1px 3px rgba(0,0,0,0.06);
    overflow: hidden;
`;

const PanelSubtitle = styled.p`
    margin: 0 0 18px 0;
    font-size: 14px;
    color: #64748b;
    font-weight: 500;
    letter-spacing: 0.01em;
`;

const TaxBlock = styled.div<{ $viewing?: boolean }>`
    padding: 16px 18px;
    background: ${p => p.$viewing ? '#eff6ff' : '#fafbfc'};
    border-radius: 8px;
    border: 1px solid ${p => p.$viewing ? '#93c5fd' : '#e2e8f0'};
    box-shadow: ${p => p.$viewing ? 'inset 3px 0 0 #3b82f6' : 'none'};
`;

const FieldsRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 20px 28px;
`;

const ViewActions = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
    margin-top: 14px;
`;

const SelfEmployedIncomeSection = (props: IProps) => {
    const [grossFocused, setGrossFocused] = useState(false);
    const currentYear = new Date().getFullYear();
    const viewingSection = props.viewingTax || props.viewingAffidavit;

    const handleTaxYearChange = (e: React.FormEvent<HTMLInputElement & FormControl>) => {
        const digits = e.currentTarget.value.replace(/\D/g, '').slice(0, 4);
        const parsed = digits === '' ? 0 : parseInt(digits, 10);
        const year = isNaN(parsed) ? 0 : Math.min(parsed, currentYear);
        props.onTaxYearChange(year);
    };

    const handleTaxGrossChange = (e: React.FormEvent<HTMLInputElement & FormControl>) => {
        props.onTaxGrossChange(parseMoneyInput(e.currentTarget.value));
    };

    return (
        <FormWrapper>
            <CardPanel>
                <Panel.Body style={{ padding: '20px 24px' }}>
                    <PanelSubtitle>
                        {props.hasAffidavit
                            ? 'Enter tax year and gross from the return, then review the affidavit if needed.'
                            : 'Enter tax year and gross from the tax return.'}
                    </PanelSubtitle>

                    <TaxBlock $viewing={viewingSection}>
                        <FieldsRow>
                            <FormGroup style={{ marginBottom: 0, flexShrink: 0 }}>
                                <ControlLabel>Tax Year</ControlLabel>
                                <FormControl
                                    id='incomeIrsYear'
                                    name='incomeIrsYear'
                                    type='text'
                                    inputMode='numeric'
                                    maxLength={4}
                                    value={props.taxYear || ''}
                                    style={{ width: 110 }}
                                    onChange={handleTaxYearChange} />
                            </FormGroup>
                            <FormGroup style={{ marginBottom: 0, flexShrink: 0 }}>
                                <ControlLabel>Tax Year Gross</ControlLabel>
                                <InputGroup>
                                    <InputGroup.Addon>$</InputGroup.Addon>
                                    <FormControl
                                        id='incomeIrsYearGross'
                                        name='incomeIrsYearGross'
                                        type='text'
                                        inputMode='decimal'
                                        style={{ width: 160 }}
                                        value={grossFocused
                                            ? (props.taxGross ? String(props.taxGross) : '')
                                            : formatMoneyInput(props.taxGross)}
                                        onFocus={() => setGrossFocused(true)}
                                        onBlur={() => setGrossFocused(false)}
                                        onChange={handleTaxGrossChange} />
                                </InputGroup>
                            </FormGroup>
                        </FieldsRow>

                        <ViewActions>
                            <Button
                                className={props.viewingTax ? 'selected-btn' : ''}
                                disabled={!props.hasTaxDoc}
                                onClick={() => props.onOpenDoc(FileDocumentType.Tax)}>
                                {props.viewingTax ? 'Viewing Tax Return' : 'View Tax Return'}
                            </Button>
                            {props.hasAffidavit && (
                                <Button
                                    className={props.viewingAffidavit ? 'selected-btn' : ''}
                                    onClick={() => props.onOpenDoc(FileDocumentType.DsSelfEmployedAffidavit)}>
                                    {props.viewingAffidavit ? 'Viewing Affidavit' : 'View Affidavit'}
                                </Button>
                            )}
                            {!props.hasTaxDoc && (
                                <Alert bsStyle='warning' style={{ marginBottom: 0, borderRadius: 6, padding: '8px 12px' }}>
                                    Tax return not found.
                                </Alert>
                            )}
                        </ViewActions>
                    </TaxBlock>

                    {props.missingDocs.length > 0 && (
                        <Alert bsStyle='danger' style={{ marginTop: 16, marginBottom: 0, borderRadius: 6 }}>
                            Cannot verify until the following are available: {props.missingDocs.join(', ')}.
                        </Alert>
                    )}
                </Panel.Body>
            </CardPanel>

            <VerifyIncomeActions
                onDecline={props.onDecline}
                onVerify={props.onVerify}
                verifyDisabled={!props.canVerify}
            />
        </FormWrapper>
    );
};

export default SelfEmployedIncomeSection;
